import React, { useState } from 'react';
import type { DateRange } from '../types';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Calendar, Button, Popover, PopoverContent, PopoverTrigger } from '@t-one-internal/design-system';
import { DateTime } from 'luxon';

interface DateRangePickerProps {
    value: DateRange;
    onChange: (value: DateRange) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange }) => {
    const [startDateOpen, setStartDateOpen] = useState(false);
    const [endDateOpen, setEndDateOpen] = useState(false);

    const formatDateForDisplay = (dateString: string | undefined): string => {
        if (!dateString) return 'Select date';
        const dt = DateTime.fromISO(dateString);
        return dt.isValid ? dt.toFormat('dd MMM yyyy') : 'Select date';
    };

    const parseToDate = (dateString: string | undefined): Date | undefined => {
        if (!dateString) return undefined;
        const dt = DateTime.fromISO(dateString);
        return dt.isValid ? dt.toJSDate() : undefined;
    };

    const handleTypeChange = (type: DateRange['type']) => {
        let startDate = '';
        let endDate = new Date().toISOString().split('T')[0];

        const today = new Date();
        if (type === '7d') {
            const past = new Date();
            past.setDate(today.getDate() - 7);
            startDate = past.toISOString().split('T')[0];
        } else if (type === '30d') {
            const past = new Date();
            past.setDate(today.getDate() - 30);
            startDate = past.toISOString().split('T')[0];
        } else if (type === 'all_time') {
            startDate = '1970-01-01';
        } else if (type === 'from_date') {
            // Default to start of this month if no start date set, or keep existing
            if (value.startDate && value.startDate !== '1970-01-01') {
                startDate = value.startDate;
            } else {
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                startDate = firstDay.toISOString().split('T')[0];
            }
        } else {
            // Keep existing custom dates if available, or default to today
            startDate = value.startDate || today.toISOString().split('T')[0];
        }

        onChange({ type, startDate, endDate });
    };

    const handleDateChange = (field: 'startDate' | 'endDate', date: Date | undefined) => {
        if (!date) return;
        const dt = DateTime.fromJSDate(date);
        const val = dt.toFormat('yyyy-MM-dd');

        // If currently 'from_date' and modifying startDate, stay in 'from_date' mode
        if (value.type === 'from_date' && field === 'startDate') {
            onChange({ ...value, startDate: val });
            setStartDateOpen(false);
            return;
        }
        // Otherwise switch to custom
        onChange({ ...value, type: 'custom', [field]: val });
        if (field === 'startDate') {
            setStartDateOpen(false);
        } else {
            setEndDateOpen(false);
        }
    };

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Date Range</label>
            <div className="flex gap-2 flex-wrap">
                {(['7d', '30d', 'all_time', 'from_date', 'custom'] as const).map((type) => (
                    <button
                        key={type}
                        type="button"
                        onClick={() => handleTypeChange(type)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${value.type === type
                            ? 'bg-white text-indigo-600'
                            : 'text-gray-500 hover:text-gray-700'
                            }`}
                    >
                        {type === '7d' ? 'Last 7 Days' :
                            type === '30d' ? 'Last 30 Days' :
                                type === 'all_time' ? 'All Time' :
                                    type === 'from_date' ? 'From Date' : 'Custom'}
                    </button>
                ))}
            </div>

            {(value.type === 'custom' || value.type === 'from_date') && (
                <div className="flex items-center gap-3 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="w-[180px]">
                        <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="w-full justify-between font-normal"
                                >
                                    <span className={!value.startDate ? 'text-muted-foreground' : ''}>
                                        {formatDateForDisplay(value.startDate)}
                                    </span>
                                    <CalendarIcon size={16} className="shrink-0 text-gray-400" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={parseToDate(value.startDate)}
                                    onSelect={(date) => handleDateChange('startDate', date)}
                                    disabled={(date) => value.endDate ? date > new Date(value.endDate) : false}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <span className="text-gray-400 text-sm">to</span>
                    <div className="w-[180px]">
                        {value.type === 'from_date' ? (
                            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500 italic flex items-center justify-between cursor-not-allowed h-9">
                                <span>Today</span>
                                <CalendarIcon size={16} className="text-gray-400 opacity-50" />
                            </div>
                        ) : (
                            <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="w-full justify-between font-normal"
                                    >
                                        <span className={!value.endDate ? 'text-muted-foreground' : ''}>
                                            {formatDateForDisplay(value.endDate)}
                                        </span>
                                        <CalendarIcon size={16} className="shrink-0 text-gray-400" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={parseToDate(value.endDate)}
                                        onSelect={(date) => handleDateChange('endDate', date)}
                                        disabled={(date) => value.startDate ? date < new Date(value.startDate) : false}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        )}
                    </div>
                </div>
            )}

            {value.type !== 'custom' && value.type !== 'from_date' && (
                <p className="text-xs text-gray-500 flex items-center gap-1">
                    <CalendarIcon size={12} />
                    {value.type === 'all_time' ? 'All available history' : `${value.startDate} — ${value.endDate}`}
                </p>
            )}
        </div>
    );
};

export default DateRangePicker;
